import { ChangeDetectionStrategy, Component, computed, DestroyRef, HostListener, inject, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ImageModule } from 'primeng/image';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { DrawerModule } from 'primeng/drawer';
import { MenuItem } from 'primeng/api';
import { filter, interval, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from './auth/auth.service';
import { CustomerService } from './customer/customer.service';
import { Customer } from './customer/customer.model';
import { FcmService } from './services/fcm.service';
import { UserActivityService } from './services/user-activity.service';
import { tap } from 'rxjs/operators';
import { LanguageService } from './services/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from './helpers/session-storage.service';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { environment } from '../environments/environment';
import { PwaInstallService } from './services/pwa-install.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    ImageModule,
    AvatarModule,
    MenuModule,
    DrawerModule,
    TranslateModule
  ],
})
export class AppComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly customerService = inject(CustomerService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fcmService = inject(FcmService);
  private readonly userActivityService = inject(UserActivityService);
  protected readonly languageService = inject(LanguageService);
  private readonly translate = inject(TranslateService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly googleAnalytics = inject(GoogleAnalyticsService);
  protected readonly pwaInstall = inject(PwaInstallService);

  isLoggedIn = signal(false);
  customer = signal<Customer | null>(null);
  isUpdateAvailable = signal(false);
  isAuthPage = signal(false);
  currentUrl = signal('');
  isDesktopNavExpanded = signal(false);
  isMobileNavVisible = signal(false);
  readonly isPwaInstalled = this.pwaInstall.isPwaInstalled;
  readonly canInstallPwa = this.pwaInstall.canInstallPwa;
  navSearchQuery = signal('');
  expandedMenuKeys = signal<Record<string, boolean>>({});

  // Computed signals for localized menus
  shellMenuItems = computed<MenuItem[]>(() => {
    // This trigger ensures re-computation when language changes
    this.languageService.currentLanguage();

    return [
      this.createMenuItem(this.translate.instant('MENU.AI_BROKER'), '/ai-broker', 'fa-solid fa-robot'),
      this.createMenuItem(this.translate.instant('MENU.DIRECTORY'), '/directory', 'fa-solid fa-book', ['/directory']),
      {
        label: this.translate.instant('MENU.LOADS'),
        icon: 'fa-solid fa-truck-ramp-box',
        items: [
          this.createMenuItem(this.translate.instant('MENU.FIND_LOADS'), '/loads', 'fa-solid fa-magnifying-glass', ['/loads', '/load-details']),
          this.createMenuItem(this.translate.instant('MENU.POST_LOAD'), '/load/create', 'fa-solid fa-plus'),
        ],
      },
      {
        label: this.translate.instant('MENU.AVAILABILITIES'),
        icon: 'fa-solid fa-calendar-day',
        items: [
          this.createMenuItem(this.translate.instant('MENU.FIND_AVAILABILITY'), '/availabilities', 'fa-solid fa-search', ['/availabilities', '/availability-details']),
          this.createMenuItem(this.translate.instant('MENU.POST_AVAILABILITY'), '/fleet/post-availability', 'fa-solid fa-plus'),
        ],
      },
      {
        label: this.translate.instant('MENU.MATCHES'),
        icon: 'fa-solid fa-handshake',
        items: [
          this.createMenuItem(this.translate.instant('MENU.ALL_MATCHES'), '/matches', 'fa-solid fa-list-ul', ['/matches']),
          this.createMenuItem(this.translate.instant('MENU.CREATE_MATCH'), '/matches/create', 'fa-solid fa-plus'),
        ],
      },
      this.createMenuItem(this.translate.instant('MENU.SHIPMENTS'), '/shipments', 'fa-solid fa-boxes-packing'),
      {
        label: this.translate.instant('MENU.FLEET'),
        icon: 'fa-solid fa-truck',
        items: [
          this.createMenuItem(this.translate.instant('MENU.FLEET_DASHBOARD'), '/fleet', 'fa-solid fa-chart-line'),
          this.createMenuItem(this.translate.instant('MENU.DRIVERS'), '/fleet/drivers', 'fa-solid fa-users-gear'),
          this.createMenuItem(this.translate.instant('MENU.VEHICLES'), '/fleet/vehicles', 'fa-solid fa-truck'),
        ],
      },
      this.createMenuItem(this.translate.instant('MENU.COMPANY'), '/company', 'fa-solid fa-building', ['/company', '/company/employees']),
    ];
  });

  filteredShellMenuItems = computed<MenuItem[]>(() => {
    const query = this.navSearchQuery().trim().toLocaleLowerCase();
    const items = this.shellMenuItems();

    if (!query) {
      return items;
    }

    return items.reduce<MenuItem[]>((matches, item) => {
      const children = this.getChildItems(item);
      const parentMatches = this.menuItemMatchesQuery(item, query);
      const matchedChildren = children.filter(child => this.menuItemMatchesQuery(child, query));

      if (parentMatches || matchedChildren.length > 0) {
        matches.push({
          ...item,
          items: parentMatches ? children : matchedChildren
        });
      }

      return matches;
    }, []);
  });

  currentPageTitle = computed(() => {
    const activeItem = this.findActiveMenuItem(this.shellMenuItems());
    return activeItem?.label ?? this.translate.instant('APP.NAME');
  });

  profileMenuItems = computed<MenuItem[]>(() => {
    const currentLanguage = this.languageService.currentLanguage();

    return [
      {
        label: this.translate.instant('MENU.PROFILE'),
        icon: 'fa-regular fa-user',
        command: () => this.navigateTo('/profile')
      },
      {
        label: this.translate.instant('MENU.SETTINGS'),
        icon: 'fa-solid fa-gear',
        command: () => this.navigateTo('/profile/edit')
      },
      { separator: true },
      {
        label: this.translate.instant('MENU.ENGLISH'),
        icon: currentLanguage === 'en' ? 'fa-solid fa-check' : 'fa-solid fa-language',
        command: () => this.changeLanguage('en'),
        disabled: currentLanguage === 'en'
      },
      {
        label: this.translate.instant('MENU.ARABIC'),
        icon: currentLanguage === 'ar' ? 'fa-solid fa-check' : 'fa-solid fa-language',
        command: () => this.changeLanguage('ar'),
        disabled: currentLanguage === 'ar'
      },
      { separator: true },
      {
        label: this.translate.instant('MENU.LOGOUT'),
        icon: 'fa-solid fa-right-from-bracket',
        command: () => this.logout(),
      },
    ];
  });

  ngOnInit() {
    this.currentUrl.set(this.router.url);
    this.isAuthPage.set(this.isNoTimeoutAuthRoute(this.router.url));
    this.pwaInstall.refreshInstallState();

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => {
      this.currentUrl.set(event.urlAfterRedirects);
      this.isAuthPage.set(this.isNoTimeoutAuthRoute(event.urlAfterRedirects));
      this.isMobileNavVisible.set(false);

      if (!this.isPublicOrAuthRoute(event.urlAfterRedirects) && this.isLoggedIn() && !this.customer()) {
        this.loadCurrentCustomer().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(customer => {
          this.customer.set(customer);
          if (customer) {
            this.syncAnalyticsUserContext(customer);
          }
        });
      }
    });

    this.authService.userData
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(user => {
          if (this.isAuthenticatedAppUser(user) && !this.isPublicOrAuthRoute(this.router.url)) {
            this.fcmService.requestPermission().then(() => {
              this.fcmService.registerCurrentToken();
            });
          }
        }),
        switchMap(user => {
          const isAppUser = this.isAuthenticatedAppUser(user);
          this.isLoggedIn.set(isAppUser);
          if (!user || !isAppUser || this.isPublicOrAuthRoute(this.router.url)) {
            this.customer.set(null);
            this.clearAnalyticsUserContext();
            return of(null);
          }

          return this.loadCurrentCustomer(user.uid);
        })
      )
      .subscribe(customer => {
        this.customer.set(customer);
        if (customer) {
          this.syncAnalyticsUserContext(customer);
        } else {
          this.clearAnalyticsUserContext();
        }
      });

    // Proactive Inactivity Check
    interval(60000).pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(() => this.isLoggedIn() && !this.isPublicOrAuthRoute(this.router.url)),
      filter(() => !this.userActivityService.isActive())
    ).subscribe(() => {
      console.warn('Inactivity detected, logging out...');
      this.logout();
    });
  }

  @HostListener('window:beforeinstallprompt', ['$event'])
  handleBeforeInstallPrompt(event: Event): void {
    this.pwaInstall.handleBeforeInstallPrompt(event);
  }

  @HostListener('window:appinstalled')
  handleAppInstalled(): void {
    this.pwaInstall.handleAppInstalled();
  }

  updateApp(): void {
    window.location.reload();
  }

  async promptInstallPwa(): Promise<void> {
    await this.pwaInstall.promptInstall();
  }

  toggleDesktopNav(): void {
    const nextExpanded = !this.isDesktopNavExpanded();
    this.isDesktopNavExpanded.set(nextExpanded);

    if (!nextExpanded) {
      this.expandedMenuKeys.set({});
    }
  }

  updateNavSearch(query: string): void {
    this.navSearchQuery.set(query);
  }

  clearNavSearch(): void {
    this.navSearchQuery.set('');
  }

  toggleMenuGroup(item: MenuItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.getChildItems(item).length === 0) {
      return;
    }

    const key = this.getMenuKey(item);
    this.expandedMenuKeys.update(keys => ({
      ...keys,
      [key]: !this.isMenuExpanded(item)
    }));
  }

  isMenuExpanded(item: MenuItem): boolean {
    if (this.navSearchQuery().trim()) {
      return true;
    }

    const key = this.getMenuKey(item);

    if (this.expandedMenuKeys()[key] !== undefined) {
      return this.expandedMenuKeys()[key];
    }

    return this.isActiveMenuItem(item);
  }

  openMobileNav(): void {
    this.isMobileNavVisible.set(true);
  }

  closeMobileNav(): void {
    this.isMobileNavVisible.set(false);
  }

  openSupportChat(): void {
    this.router.navigate(['/ai-broker'], {
      queryParams: {
        support: 1,
        source: 'appbar'
      }
    });
  }

  navigateTo(url: string): void {
    void this.router.navigateByUrl(url);
  }

  changeLanguage(language: 'en' | 'ar'): void {
    if (this.languageService.currentLanguage() !== language) {
      this.languageService.setLanguage(language);
    }
  }

  drawerPosition(): 'left' | 'right' {
    return this.languageService.currentLanguage() === 'ar' ? 'right' : 'left';
  }

  isActiveMenuItem(item: MenuItem): boolean {
    const activeItem = this.findActiveMenuItem(this.shellMenuItems());

    if (!activeItem) {
      return false;
    }

    if (this.getMenuKey(activeItem) === this.getMenuKey(item)) {
      return true;
    }

    if (this.getChildItems(item).length === 0) {
      return false;
    }

    return this.getChildItems(item).some(child => this.getMenuKey(child) === this.getMenuKey(activeItem));
  }

  getChildItems(item: MenuItem): MenuItem[] {
    return Array.isArray(item.items) ? item.items as MenuItem[] : [];
  }

  getRouterPath(item: MenuItem): string {
    const routerLink = item.routerLink;

    if (Array.isArray(routerLink)) {
      return routerLink.join('');
    }

    return typeof routerLink === 'string' ? routerLink : '';
  }

  private createMenuItem(label: string, routerLink: string, icon: string, activePaths: string[] = [routerLink]): MenuItem {
    return {
      label,
      routerLink: [routerLink],
      icon,
      state: {
        activePaths
      }
    };
  }

  private findActiveMenuItem(items: MenuItem[]): MenuItem | null {
    return this.flattenMenuItems(items)
      .map(item => ({
        item,
        score: this.getRouteMatchScore(item)
      }))
      .filter(match => match.score > 0)
      .sort((a, b) => b.score - a.score)[0]?.item ?? null;
  }

  private flattenMenuItems(items: MenuItem[]): MenuItem[] {
    return items.flatMap(item => {
      const children = this.getChildItems(item);
      return children.length > 0 ? this.flattenMenuItems(children) : [item];
    });
  }

  private getRouteMatchScore(item: MenuItem): number {
    const normalizedUrl = this.currentUrl().split('?')[0].split('#')[0];
    const activePaths = this.getActivePaths(item);

    return activePaths.reduce((bestScore, target) => {
      if (!this.isRouteActive(normalizedUrl, target)) {
        return bestScore;
      }

      const exactBonus = normalizedUrl === target ? 1000 : 0;
      return Math.max(bestScore, target.length + exactBonus);
    }, 0);
  }

  private getActivePaths(item: MenuItem): string[] {
    const state = item.state as { activePaths?: string[] } | undefined;
    return state?.activePaths?.length ? state.activePaths : [this.getRouterPath(item)].filter(Boolean);
  }

  private getMenuKey(item: MenuItem): string {
    return this.getRouterPath(item) || item.label || '';
  }

  private menuItemMatchesQuery(item: MenuItem, query: string): boolean {
    return `${item.label ?? ''} ${this.getRouterPath(item)}`
      .toLocaleLowerCase()
      .includes(query);
  }

  private isRouteActive(currentUrl: string, target: string): boolean {
    if (!target) {
      return false;
    }

    return currentUrl === target || currentUrl.startsWith(`${target}/`);
  }

  private isNoTimeoutAuthRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/login' || path === '/register' || path === '/forgot-password';
  }

  private isPublicOrAuthRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/' || path.startsWith('/public/') || this.isNoTimeoutAuthRoute(url);
  }

  private isAuthenticatedAppUser(user: any): boolean {
    return !!user && user.isAnonymous !== true;
  }

  private loadCurrentCustomer(firebaseId = this.authService.getCurrentUser()?.uid) {
    if (!firebaseId) {
      return of(null);
    }

    return this.customerService.getCustomerByFirebaseId(firebaseId).pipe(
      map(response => response.data as Customer),
      tap(customer => {
        if (customer) {
          this.sessionStorageService.saveCustomer(customer);
        }
      }),
      catchError(() => of(null))
    );
  }

  private syncAnalyticsUserContext(customer: Customer | null): void {
    const user = this.authService.getCurrentUser();

    if (!customer || !user) {
      this.clearAnalyticsUserContext();
      return;
    }

    const company = customer.company ?? this.sessionStorageService.getCompany() ?? null;
    const roleNames = this.resolveRoleNames(customer);
    const providerIds = this.resolveProviderIds(user);

    this.googleAnalytics.gtag('set', {
      user_id: String(customer.id ?? user.uid ?? ''),
      user_properties: {
        app: 'nexus-haul-portal',
        environment: environment.production ? 'prod' : 'dev',
        firebase_uid: String(user.uid ?? ''),
        auth_provider: providerIds[0] ?? 'unknown',
        auth_provider_count: String(providerIds.length),
        customer_id: String(customer.id ?? ''),
        customer_name: String(customer.name ?? ''),
        company_id: String(company?.id ?? ''),
        company_name: String(company?.name ?? ''),
        company_country_id: String(company?.countryId ?? ''),
        company_enterprise: String(!!company?.enableEnterpriseFeatures),
        corporate_role: String(customer.corporateRole ?? ''),
        role_names: roleNames.join('|'),
        role_count: String(roleNames.length),
        customer_country_id: String(customer.countryId ?? ''),
        default_language: String(customer.defaultLanguage ?? ''),
        is_phone_verified: String(!!customer.isPhoneVerified),
        has_phone: String(!!customer.phoneNumber),
        has_email: String(!!customer.emailAddress),
        email_domain: this.extractEmailDomain(customer.emailAddress ?? user.email ?? undefined),
      }
    });
  }

  private clearAnalyticsUserContext(): void {
    this.googleAnalytics.gtag('set', {
      user_id: null,
      user_properties: null
    });
  }

  private resolveRoleNames(customer: Customer): string[] {
    const roles = Array.isArray(customer.roles)
      ? customer.roles.map((role: any) => role?.roleName ?? role?.name ?? String(role)).filter(Boolean)
      : [];

    return Array.from(new Set(roles));
  }

  private resolveProviderIds(user: any): string[] {
    return Array.isArray(user?.providerData)
      ? user.providerData
        .map((provider: any) => provider?.providerId)
        .filter((providerId: string | undefined): providerId is string => !!providerId)
      : [];
  }

  private extractEmailDomain(email?: string): string {
    if (!email || !email.includes('@')) {
      return '';
    }

    return email.split('@').pop() ?? '';
  }

  logout(): void {
    this.clearAnalyticsUserContext();
    this.authService.logout().subscribe(() => {
      this.sessionStorageService.signOut();
      this.customer.set(null);
      this.isLoggedIn.set(false);
      this.router.navigate(['/login']);
    });
  }
}
